// Chat Widget config (PRD §4.4). A floating WhatsApp button the tenant embeds on
// their website; it deep-links to wa.me with a prefilled message. Config is read
// from tenants/{t}/config/chatWidget; the connected number is used as the default
// destination. Saved via the chatWidgetSave callable (owner/admin).
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useTenant } from './tenant';

export type WidgetPosition = 'right' | 'left';
export interface ChatWidgetConfig {
  enabled: boolean;
  phoneE164: string;
  prefill: string;
  greeting: string;
  color: string;
  position: WidgetPosition;
}

export const DEFAULT_WIDGET: ChatWidgetConfig = {
  enabled: false,
  phoneE164: '',
  prefill: 'Hi! I have a question.',
  greeting: 'Chat with us',
  color: '#16A34A',
  position: 'right',
};

export function useChatWidget(): { config: ChatWidgetConfig; loading: boolean } {
  const { tenantId } = useTenant();
  const [config, setConfig] = useState<ChatWidgetConfig>(DEFAULT_WIDGET);
  const [loading, setLoading] = useState(true);

  // Hold both the config doc and the connected number in state, then derive the
  // effective config — avoids a race between the two snapshot listeners where the
  // fallback number was read before it had loaded.
  const [raw, setRaw] = useState<Partial<ChatWidgetConfig> | null>(null);
  const [connectedPhone, setConnectedPhone] = useState('');

  useEffect(() => {
    if (!tenantId) { setRaw(null); setConnectedPhone(''); setLoading(false); return; }
    const unsubInt = onSnapshot(doc(db, `tenants/${tenantId}/integrations/primary`), (snap) => {
      setConnectedPhone((snap.data()?.verifiedPhoneE164 as string) ?? '');
    }, () => {});
    const unsubCfg = onSnapshot(doc(db, `tenants/${tenantId}/config/chatWidget`), (snap) => {
      setRaw((snap.data() as Partial<ChatWidgetConfig>) ?? {});
      setLoading(false);
    }, () => setLoading(false));
    return () => { unsubInt(); unsubCfg(); };
  }, [tenantId]);

  useEffect(() => {
    const d = raw ?? {};
    setConfig({
      enabled: d.enabled ?? DEFAULT_WIDGET.enabled,
      phoneE164: d.phoneE164 || connectedPhone || '',
      prefill: d.prefill ?? DEFAULT_WIDGET.prefill,
      greeting: d.greeting ?? DEFAULT_WIDGET.greeting,
      color: d.color ?? DEFAULT_WIDGET.color,
      position: (d.position as WidgetPosition) ?? DEFAULT_WIDGET.position,
    });
  }, [raw, connectedPhone]);

  return { config, loading };
}

const saveFn = httpsCallable<ChatWidgetConfig, { ok: boolean }>(functions, 'chatWidgetSave');
export function saveChatWidget(config: ChatWidgetConfig): Promise<{ ok: boolean }> {
  return saveFn(config).then((r) => r.data);
}

/** Build the self-contained embed snippet the tenant pastes on their website. */
export function widgetSnippet(c: ChatWidgetConfig): string {
  const digits = c.phoneE164.replace(/\D/g, '');
  const text = encodeURIComponent(c.prefill);
  const side = c.position === 'left' ? 'left:20px' : 'right:20px';
  return `<!-- WaTag chat widget -->
<script>(function(){
  var a=document.createElement('a');
  a.href='https://wa.me/${digits}?text=${text}';
  a.target='_blank';a.rel='noopener';a.title=${JSON.stringify(c.greeting)};
  a.style.cssText='position:fixed;bottom:20px;${side};z-index:9999;width:60px;height:60px;border-radius:50%;background:${c.color};display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,.25);text-decoration:none';
  a.innerHTML='<svg width="32" height="32" viewBox="0 0 24 24" fill="white"><path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4-1.1l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 1 1 12 20zm4.4-6c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 0 1-3.2-2.8c-.1-.2 0-.4.1-.5l.4-.5.2-.4v-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3A2.9 2.9 0 0 0 6 8.9c0 1.7 1.2 3.3 1.4 3.5.2.2 2.4 3.7 5.8 5 .8.3 1.4.5 1.9.7.8.2 1.5.2 2.1.1.6-.1 1.4-.6 1.6-1.2.2-.6.2-1 .1-1.2l-.6-.5z"/></svg>';
  document.body.appendChild(a);
})();</script>`;
}
